import { useMemo, useState } from 'react';
import { inTeam, settingsTeamFilter } from '../use-teams';
import type {
  WorkScheduleAssignment,
  WorkScheduleTemplate,
  WorkScheduleType,
} from '@forecastify/shared';
import { Select, Stepper, Switch, Tabs, TextInput, type MenuItem } from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { DateField } from '../date-field';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { Field } from '../card';
import { identityColor, initialsOf } from '../identity';
import { EditorDialog } from './editor-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Munkarendek. C43, two tabs over one subject.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **Sablonok** — reusable shapes the workspace curates. **Beosztások** — who is on which, from a
 * date.
 *
 * ⚠️⚠️ **REBUILT AGAINST THE MOCKUP, 11 Sep 2026** — *"ours is plain and boring, the mockup is
 * better"*. Three things changed, and each is a decision rather than a paint job:
 *
 * 1. **The seven days are COLUMNS in both tabs.** A schedule's whole content is its week, and a row
 *    that says only *"Full-time · 40h"* makes the reader open a drawer to learn whether Friday is a
 *    working day. Fourteen small numbers replace fourteen clicks.
 * 2. ⚠️⚠️ **THE ASSIGNMENTS TAB IS PERSON-CENTRIC, AND OURS LISTED ASSIGNMENT ROWS.** A schedule is
 *    a timeline (C43), so one person can own five rows — and a tab that lists them all answers
 *    *"what happened"* when the question it is opened with is *"who is on what"*. The mockup lists
 *    every member with the schedule in force TODAY, which is also what makes *"n hiányzik"*
 *    countable: somebody with no schedule is a row here rather than an absence nobody can see. The
 *    history is not lost — it is on the person's own screen, where a timeline belongs.
 * 3. **A kebab, not two naked icon buttons.** The mockup's menu carries five verbs, three of which
 *    we had nowhere at all: duplicate, assign-to-a-person, and activate/deactivate.
 *
 * ⚠️ **The `#` column is the sort ordinal, not an id.** The mockup prints a short numeric id; ours
 * are uuids, and eight hex characters is a column nobody can read or quote.
 *
 * ⚠️ **A `flex` row draws em-dashes across its seven days, not zeroes.** Zero would say *"no hours
 * expected on Monday"*; the truth is that a flex schedule has nothing to say about Monday, which is
 * the distinction P3-7 turns on.
 */

/**
 * Monday first, like `wireDayOfWeek` and like the stored array.
 *
 * ⚠️ **`Intl`, not fourteen catalogue keys.** `time-screen.tsx` sets the rule for dates: the app has
 * a locale, and a hand-written weekday table is a second thing to get wrong in two languages. The
 * anchor is a known Monday, so the seven come out in the order the array is stored in.
 */
function dayNames(locale: string): readonly string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
    /* 2024-01-01 was a Monday. UTC, so a negative timezone offset cannot walk it back to Sunday. */
    format.format(new Date(Date.UTC(2024, 0, 1 + offset))),
  );
}

export interface TemplateDraft {
  readonly id: string | null;
  readonly name: string;
  readonly type: WorkScheduleType;
  readonly days: readonly number[];
  readonly weeklyHours: number;
  readonly active: boolean;
  readonly version: number;
}

export interface AssignmentDraft {
  readonly memberId: string;
  readonly fromDate: string;
  /** `null` means a custom shape — the two arms the wire schema refuses to mix. */
  readonly templateId: string | null;
  readonly type: WorkScheduleType;
  readonly days: readonly number[];
  readonly weeklyHours: number;
  readonly version: number | null;
}

const EMPTY_DAYS = [0, 0, 0, 0, 0, 0, 0] as const;
const FULL_TIME = [8, 8, 8, 8, 8, 0, 0] as const;

/** ⚠️ One builder per kind, so a row's name and its kebab cannot open two different drafts. */
function templateDraftOf(row: WorkScheduleTemplate): TemplateDraft {
  return {
    id: row.id,
    name: row.name,
    type: row.type,
    days: row.days.map(Number),
    weeklyHours: Number(row.weeklyHours),
    active: row.active,
    version: row.version,
  };
}

/**
 * ⚠️⚠️ **AN ASSIGNMENT HAD NO EDIT PATH AT ALL — ONLY A BIN.** Correcting somebody's start date
 * meant deleting the row and building it again, which loses the history the timeline exists to
 * show. The save verb is an upsert on `(member, from_date)` (C43: *"one verb that replaces"*), so
 * editing is the same drawer, pre-filled — and changing the DATE writes a new row rather than
 * moving the old one, which is the timeline behaving exactly as it should.
 */
function assignmentDraftOf(row: WorkScheduleAssignment): AssignmentDraft {
  return {
    memberId: row.memberId,
    fromDate: row.fromDate,
    templateId: row.templateId,
    type: row.type,
    days: row.days.map(Number),
    weeklyHours: Number(row.weeklyHours),
    version: row.version,
  };
}

/** The sum of a fix schedule's days — what the row shows as its weekly figure. */
function sum(days: readonly (string | number)[]): number {
  return days.reduce((total: number, value) => total + (Number(value) || 0), 0);
}

export function SchedulesSection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly templates: readonly WorkScheduleTemplate[];
  readonly assignments: readonly WorkScheduleAssignment[];
  readonly members: readonly {
    readonly id: string;
    readonly name: string;
    /** ⚠️ C68 · the generated face's seed. `null` where the person has no address. */
    readonly avatarSeed: string | null;
  }[];
  /** R-60 · the team filter, on the client: the workspace's teams and who is in which. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly memberTeams?: ReadonlyMap<string, readonly string[]>;
  readonly canManage: boolean;
  readonly serverError: string | null;
  readonly onSaveTemplate: (draft: TemplateDraft) => Promise<boolean>;
  readonly onDeleteTemplate: (template: WorkScheduleTemplate) => void;
  readonly onSaveAssignment: (draft: AssignmentDraft) => Promise<boolean>;
  readonly onDeleteAssignment: (assignment: WorkScheduleAssignment) => void;
  /**
   * Opens somebody's profile from the assignment row's menu. Absent for a reader with no people
   * permission — a menu entry that lands on a refusal is worse than no menu entry.
   */
  readonly onOpenPerson?: (memberId: string) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const { locale } = useI18n();
  const days = dayNames(locale);

  const [tab, setTab] = useState<'templates' | 'assignments'>('templates');
  const [scheduleFilter, setScheduleFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'template' | 'custom' | ''>('');
  const [teamId, setTeamId] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<WorkScheduleType | ''>('');
  const [status, setStatus] = useState<'' | 'active' | 'inactive'>('');
  const [assignQuery, setAssignQuery] = useState('');
  const [template, setTemplate] = useState<TemplateDraft | null>(null);
  const [assignment, setAssignment] = useState<AssignmentDraft | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ EVERY ASSIGNMENT, PLUS A ROW FOR EACH PERSON WHO HAS NONE.           │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **A synthetic id for the empty rows** — `missing:<memberId>` — because `DataList` keys on it
   * and a person with no assignment has no row of their own to borrow one from. The prefix is what
   * keeps it from ever colliding with a uuid.
   */
  const assignmentList = useMemo(() => {
    const byMember = new Map<string, WorkScheduleAssignment[]>();
    for (const row of props.assignments) {
      byMember.set(row.memberId, [...(byMember.get(row.memberId) ?? []), row]);
    }

    return props.members.flatMap((member) => {
      const held = byMember.get(member.id) ?? [];
      if (held.length === 0) {
        return [
          {
            id: `missing:${member.id}`,
            memberId: member.id,
            memberName: member.name,
            avatarSeed: member.avatarSeed,
            assignment: null as WorkScheduleAssignment | null,
          },
        ];
      }
      /* ⚠️ The empty branch above widens `assignment` to `| null` for the whole array, so this
         one needs no assertion of its own. */
      return held.map((assignment) => ({
        id: assignment.id,
        memberId: member.id,
        memberName: member.name,
        avatarSeed: member.avatarSeed,
        assignment,
      }));
    });
  }, [props.members, props.assignments]);

  const shownAssignments = useMemo(() => {
    const needle = assignQuery.trim().toLowerCase();

    return assignmentList.filter((row) => {
      if (needle !== '' && !row.memberName.toLowerCase().includes(needle)) return false;
      if (!inTeam(props.memberTeams ?? new Map(), row.memberId, teamId)) return false;

      /* ⚠️ A person with no assignment fails every assignment-shaped filter, which is right: they
         are not *"on the flexible schedule"* in any sense a filter could mean. */
      if (scheduleFilter !== '' && row.assignment?.templateId !== scheduleFilter) return false;
      if (sourceFilter !== '') {
        const isTemplate = row.assignment !== null && row.assignment.templateId !== null;
        if (row.assignment === null || isTemplate !== (sourceFilter === 'template')) return false;
      }
      return true;
    });
  }, [assignmentList, assignQuery, scheduleFilter, sourceFilter, props.memberTeams, teamId]);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C66 · THE SEVEN DAYS ARE SEVEN COLUMNS, AND THEY HAVE TO BE.         │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The mockup's grid is `34px minmax(0,1fr) 96px repeat(7,32px) 52px 92px 36px` — one narrow
   * right-aligned column per weekday, because the question a reader brings to this table is
   * *"which day is short"*, and a single *8/8/8/8/8* cell cannot be scanned down a column.
   */
  /**
   * ⚠️⚠️ **ONE ROW PER ASSIGNMENT, NOT PER PERSON.** A person with no assignment keeps a row too —
   * *who is missing one* is a real question this tab is read for, and that row is the only one
   * offering *assign* rather than *edit*.
   */
  /**
   * ⚠️⚠️ **THESE FOUR SIT ABOVE THE ROW BUILDERS, AND THAT IS NOT COSMETIC.** The rows are built
   * during render and call `templateMenu` and `assignmentMenu`; a `const` is in its temporal dead
   * zone until its own line runs, so defining them below threw *"can't access lexical declaration
   * 'templateMenu' before initialization"* and the screen never drew. Túlóra hit the same wall.
   */
  const newTemplate = (): TemplateDraft => ({
    id: null,
    name: '',
    type: 'fix',
    days: [...FULL_TIME],
    weeklyHours: 40,
    active: true,
    version: 0,
  });

  const newAssignment = (memberId: string): AssignmentDraft => ({
    memberId,
    fromDate: today,
    templateId: props.templates.find((row) => row.active)?.id ?? null,
    type: 'fix',
    days: [...FULL_TIME],
    weeklyHours: 40,
    version: null,
  });

  const templateMenu = (row: WorkScheduleTemplate): MenuItem[] => [
    {
      label: t('common.edit'),
      icon: 'pencil',
      onSelect: () => {
        setTemplate(templateDraftOf(row));
      },
    },
    {
      /* ⚠️ A copy opens as a NEW draft rather than saving one: naming it is the first decision, and
         a workspace silently gaining *"Full-time (40h) másolat"* is not what the verb promises. */
      label: t('settings.schedules.duplicate'),
      icon: 'copy',
      onSelect: () => {
        setTemplate({
          ...templateDraftOf(row),
          id: null,
          name: t('settings.schedules.copyOf', { name: row.name }),
          version: 0,
        });
      },
    },
    {
      label: t('settings.schedules.assignToPerson'),
      icon: 'users',
      onSelect: () => {
        setTab('assignments');
        setAssignment({ ...newAssignment(''), templateId: row.id });
      },
    },
    { type: 'separator' },
    {
      label: row.active ? t('settings.schedules.deactivate') : t('settings.schedules.activate'),
      icon: 'flag',
      onSelect: () => {
        void props.onSaveTemplate({ ...templateDraftOf(row), active: !row.active });
      },
    },
    {
      label: t('common.delete'),
      icon: 'trash',
      destructive: true,
      /* ⚠️ A template somebody is on cannot be deleted — the server refuses too. The menu says so
         first, rather than letting a click produce a red toast. */
      disabled: row.assignedCount > 0,
      onSelect: () => {
        props.onDeleteTemplate(row);
      },
    },
  ];

  const assignmentMenu = (
    member: { readonly id: string; readonly name: string },
    current: WorkScheduleAssignment | undefined,
  ): MenuItem[] => [
    {
      label:
        current === undefined
          ? t('settings.schedules.assign')
          : t('settings.schedules.editAssignment', { name: member.name }),
      icon: 'pencil',
      onSelect: () => {
        setAssignment(
          current === undefined ? newAssignment(member.id) : assignmentDraftOf(current),
        );
      },
    },
    ...(props.onOpenPerson === undefined
      ? []
      : [
          {
            label: t('settings.schedules.openProfile'),
            icon: 'arrow-right' as const,
            onSelect: () => {
              props.onOpenPerson?.(member.id);
            },
          },
        ]),
    { type: 'separator' },
    {
      label: t('settings.schedules.deleteAssignment', { name: member.name }),
      icon: 'trash',
      destructive: true,
      disabled: current === undefined,
      onSelect: () => {
        if (current !== undefined) props.onDeleteAssignment(current);
      },
    },
  ];

  const assignmentSettingsRows: SettingsRow[] = shownAssignments.map((row) => ({
    id: row.id,
    onOpen: !props.canManage
      ? undefined
      : () => {
          setAssignment(
            row.assignment === null
              ? newAssignment(row.memberId)
              : assignmentDraftOf(row.assignment),
          );
        },
    cells: [
      {
        kind: 'name',
        text: row.memberName,
        avatar: {
          initials: initialsOf(row.memberName),
          color: identityColor(row.memberId),
          ...(row.avatarSeed === null ? {} : { seed: row.avatarSeed }),
        },
      },
      {
        kind: 'text',
        text: row.assignment?.templateName ?? t('settings.schedules.custom'),
      },
      row.assignment === null
        ? { kind: 'blank' }
        : {
            /* ⚠️ The SOURCE is whether a template is named, not a field of its own. */
            kind: 'badge',
            text:
              row.assignment.templateId === null
                ? t('settings.schedules.custom')
                : t('settings.schedules.fromTemplate'),
            tone: row.assignment.templateId === null ? 'outline' : 'secondary',
          },
      row.assignment === null
        ? { kind: 'blank' }
        : { kind: 'num', text: String(Number(row.assignment.weeklyHours)) },
      row.assignment === null
        ? { kind: 'blank' }
        : { kind: 'mono', text: row.assignment.fromDate, className: 'text-muted-foreground' },
      !props.canManage
        ? { kind: 'blank' }
        : {
            kind: 'kebab',
            ariaLabel: t('settings.schedules.editAssignment', { name: row.memberName }),
            items: assignmentMenu(
              { id: row.memberId, name: row.memberName },
              row.assignment ?? undefined,
            ),
          },
    ],
  }));

  const templateRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return props.templates.filter((row) => {
      if (needle !== '' && !row.name.toLowerCase().includes(needle)) return false;
      if (typeFilter !== '' && row.type !== typeFilter) return false;
      if (status === 'active' && !row.active) return false;
      if (status === 'inactive' && row.active) return false;
      return true;
    });
  }, [props.templates, query, typeFilter, status]);

  /**
   * ⚠️⚠️ **THE SEVEN DAYS ARE SEVEN COLUMNS, AND THEY HAVE TO BE.** The mockup's grid is
   * `34px minmax(0,1fr) 96px repeat(7,32px) 52px 92px 36px` — one narrow right-aligned column per
   * weekday, because the question a reader brings to this table is *"which day is short"*, and a
   * single *8/8/8/8/8* cell cannot be scanned down a column.
   */
  const templateSettingsRows: SettingsRow[] = templateRows.map((row, index) => ({
    id: row.id,
    onOpen: !props.canManage
      ? undefined
      : () => {
          setTemplate(templateDraftOf(row));
        },
    cells: [
      { kind: 'index', text: index + 1 },
      /* ⚠️ The head count rides under the name — the mockup's own `sub`. It is what says whether a
         template can be deleted, and it was a column of dashes on its own. */
      {
        kind: 'name',
        text: row.name,
        sub:
          row.assignedCount === 0
            ? undefined
            : t('settings.schedules.people', { count: row.assignedCount }),
      },
      {
        kind: 'text',
        text:
          row.type === 'flex'
            ? t('settings.schedules.type.flex')
            : t('settings.schedules.type.fix'),
      },
      /* ⚠️ A flexible template has no per-day hours at all — an em-dash, not a zero. A zero would
         read as *nobody works Monday*, which is a different fact. */
      ...days.map((_, dayIndex): SettingsRow['cells'][number] =>
        row.type === 'flex' || Number(row.days[dayIndex] ?? 0) === 0
          ? { kind: 'num', text: '–', className: 'text-muted-foreground' }
          : { kind: 'num', text: String(Number(row.days[dayIndex] ?? 0)) },
      ),
      {
        kind: 'num',
        text: row.type === 'flex' ? String(Number(row.weeklyHours)) : String(sum(row.days)),
      },
      {
        kind: 'badge',
        text: row.active
          ? t('settings.schedules.status.active')
          : t('settings.schedules.status.inactive'),
        tone: row.active ? 'secondary' : 'outline',
        color: row.active ? 'var(--status-ok)' : 'var(--text-3)',
      },
      !props.canManage
        ? { kind: 'blank' }
        : {
            kind: 'kebab',
            ariaLabel: t('settings.schedules.editTemplate', { name: row.name }),
            items: templateMenu(row),
          },
    ],
  }));

  const onTemplates = tab === 'templates';

  return (
    <div data-screen="settings-schedules" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.schedules')}
        action={
          !props.canManage
            ? undefined
            : {
                label: onTemplates
                  ? t('settings.schedules.newTemplate')
                  : t('settings.schedules.assign'),
                icon: 'plus',
                onSelect: () => {
                  if (onTemplates) setTemplate(newTemplate());
                  else setAssignment(newAssignment(''));
                },
              }
        }
        tabs={
          <Tabs
            variant="underline"
            className="border-0"
            label={t('settings.nav.schedules')}
            value={tab}
            onValueChange={(value) => {
              setTab(value === 'assignments' ? 'assignments' : 'templates');
            }}
            items={[
              {
                value: 'templates',
                label: t('settings.schedules.tab.templates'),
                icon: 'calendar',
                ...(props.loading === true ? {} : { count: props.templates.length }),
              },
              {
                value: 'assignments',
                label: t('settings.schedules.tab.assignments'),
                icon: 'users',
                ...(props.loading === true ? {} : { count: props.assignments.length }),
              },
            ]}
          />
        }
        error={props.serverError}
        search={onTemplates ? query : assignQuery}
        onSearch={onTemplates ? setQuery : setAssignQuery}
        searchPlaceholder={
          onTemplates
            ? t('settings.schedules.searchTemplates')
            : t('settings.schedules.searchPeople')
        }
        filters={
          onTemplates
            ? [
                {
                  key: 'type',
                  value: typeFilter,
                  ariaLabel: t('settings.schedules.type'),
                  options: [
                    { value: '', label: t('settings.schedules.type.all') },
                    { value: 'fix', label: t('settings.schedules.type.fix') },
                    { value: 'flex', label: t('settings.schedules.type.flex') },
                  ],
                  onChange: (value) => {
                    setTypeFilter(value === 'fix' || value === 'flex' ? value : '');
                  },
                },
                {
                  key: 'status',
                  value: status,
                  ariaLabel: t('settings.schedules.status'),
                  options: [
                    { value: '', label: t('settings.schedules.status.all') },
                    { value: 'active', label: t('settings.schedules.status.active') },
                    { value: 'inactive', label: t('settings.schedules.status.inactive') },
                  ],
                  onChange: (value) => {
                    setStatus(value === 'active' || value === 'inactive' ? value : '');
                  },
                },
              ]
            : [
                {
                  key: 'schedule',
                  value: scheduleFilter,
                  ariaLabel: t('settings.schedules.column.schedule'),
                  options: [
                    { value: '', label: t('settings.schedules.type.all') },
                    ...props.templates.map((one) => ({ value: one.id, label: one.name })),
                  ],
                  onChange: setScheduleFilter,
                },
                {
                  key: 'source',
                  value: sourceFilter,
                  ariaLabel: t('settings.schedules.column.source'),
                  options: [
                    { value: '', label: t('settings.schedules.type.all') },
                    { value: 'template', label: t('settings.schedules.fromTemplate') },
                    { value: 'custom', label: t('settings.schedules.custom') },
                  ],
                  onChange: (value) => {
                    setSourceFilter(value === 'template' || value === 'custom' ? value : '');
                  },
                },
                ...settingsTeamFilter(t, props.teams ?? [], teamId, setTeamId),
              ]
        }
      >
        {onTemplates ? (
          <SettingsTable
            grid="34px minmax(0,1fr) 96px repeat(7,32px) 52px 92px 36px"
            minWidth={960}
            columns={[
              { label: '#' },
              { label: t('settings.schedules.column.name') },
              { label: t('settings.schedules.column.type') },
              ...days.map((day) => ({ label: day, align: 'right' as const })),
              { label: t('settings.schedules.column.weekly'), align: 'right' as const },
              { label: t('settings.schedules.column.status') },
              {},
            ]}
            groups={[{ rows: templateSettingsRows }]}
            loading={props.loading}
            empty={{
              icon: 'calendar',
              title: t('settings.schedules.emptyTemplates'),
              body: t('settings.schedules.emptyTemplatesBody'),
            }}
          />
        ) : (
          <SettingsTable
            grid="minmax(0,1fr) 156px 96px 52px 116px 36px"
            minWidth={800}
            columns={[
              { label: t('settings.schedules.column.person') },
              { label: t('settings.schedules.column.schedule') },
              { label: t('settings.schedules.column.source') },
              { label: t('settings.schedules.column.weekly'), align: 'right' as const },
              { label: t('settings.schedules.column.from') },
              {},
            ]}
            groups={[{ rows: assignmentSettingsRows }]}
            loading={props.loading}
            empty={{
              icon: 'users',
              title: t('settings.schedules.emptyAssignments'),
              body: t('settings.schedules.assign'),
            }}
          />
        )}
      </SettingsPage>

      {/* ── the template editor ────────────────────────────────────────────────────────────── */}
      <EditorDialog
        open={template !== null}
        title={
          template?.id === null
            ? t('settings.schedules.dialog.newTemplate')
            : t('settings.schedules.dialog.editTemplate')
        }
        /* ⚠️ Wider than the 560px default: seven steppers side by side is what this form IS, and
           at 560 the last two wrapped onto a second line. */
        width={720}
        saveDisabled={template === null || template.name.trim() === ''}
        onClose={() => {
          setSaveFailed(false);
          setTemplate(null);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          if (template === null) return;
          setSaving(true);
          void props.onSaveTemplate(template).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setTemplate(null);
          });
        }}
      >
        {template === null ? null : (
          <>
            <Field label={t('settings.schedules.templateName')} required>
              <TextInput
                value={template.name}
                placeholder={t('settings.schedules.namePlaceholder')}
                onChange={(event) => {
                  setTemplate({ ...template, name: event.target.value });
                }}
              />
            </Field>

            <TypePicker
              value={template.type}
              onChange={(type) => {
                /* ⚠️ Switching to flex zeroes the days rather than keeping them out of sight: a
                   flex template still carrying [8,8,8,8,8,0,0] would show them again the moment
                   somebody switched back, as though they had been chosen. */
                setTemplate({
                  ...template,
                  type,
                  days: type === 'flex' ? [...EMPTY_DAYS] : template.days,
                });
              }}
            />

            {template.type === 'flex' ? (
              <Field label={t('settings.schedules.weekly')} note={t('settings.schedules.flexNote')}>
                <Stepper
                  value={String(template.weeklyHours)}
                  min={0}
                  max={80}
                  step={0.5}
                  aria-label={t('settings.schedules.weekly')}
                  decrementLabel={t('common.decrease')}
                  incrementLabel={t('common.increase')}
                  onChange={(value) => {
                    setTemplate({ ...template, weeklyHours: Number(value) || 0 });
                  }}
                />
              </Field>
            ) : (
              <DayGrid
                days={template.days}
                names={days}
                onChange={(next) => {
                  setTemplate({ ...template, days: next, weeklyHours: sum(next) });
                }}
              />
            )}

            {/* ⚠️ A switch with its consequence written beside it, not a status dropdown. *Inaktív*
                is not a state somebody browses to — it is a thing that happens to new assignments,
                and the row says which. */}
            <div className="flex items-center gap-2.5 rounded-lg border p-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium">
                  {t('settings.schedules.status.active')}
                </span>
              </span>
              <Switch
                checked={template.active}
                label={t('settings.schedules.status.active')}
                onCheckedChange={(checked) => {
                  setTemplate({ ...template, active: checked });
                }}
              />
            </div>
          </>
        )}
      </EditorDialog>

      {/* ── the assignment editor ──────────────────────────────────────────────────────────── */}
      <EditorDialog
        open={assignment !== null}
        title={
          assignment?.version === null
            ? t('settings.schedules.dialog.assign')
            : t('settings.schedules.dialog.editAssignment')
        }
        width={720}
        saveDisabled={assignment === null || assignment.memberId === ''}
        onClose={() => {
          setSaveFailed(false);
          setAssignment(null);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          if (assignment === null) return;
          setSaving(true);
          void props.onSaveAssignment(assignment).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setAssignment(null);
          });
        }}
      >
        {assignment === null ? null : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('settings.schedules.column.person')} required>
                <Select
                  aria-label={t('settings.schedules.column.person')}
                  searchable
                  searchPlaceholder={t('settings.schedules.searchPeople')}
                  value={assignment.memberId}
                  onChange={(event) => {
                    setAssignment({ ...assignment, memberId: event.target.value });
                  }}
                  options={[
                    { value: '', label: t('settings.schedules.pickPerson') },
                    ...props.members.map((member) => ({ value: member.id, label: member.name })),
                  ]}
                />
              </Field>
              <Field label={t('settings.schedules.column.from')}>
                <DateField
                  label={t('settings.schedules.column.from')}
                  value={assignment.fromDate}
                  clearable={false}
                  onChange={(fromDate) => {
                    if (fromDate !== '') setAssignment({ ...assignment, fromDate });
                  }}
                />
              </Field>
            </div>

            {/* ⚠️ Source first, because the fields under it change with it — and because it is the
                one decision the wire schema refuses to blur: an assignment carries a template OR a
                shape of its own, never both. */}
            <RadioCards
              label={t('settings.schedules.column.source')}
              value={assignment.templateId === null ? 'custom' : 'template'}
              options={[
                {
                  value: 'template',
                  title: t('settings.schedules.fromTemplate'),
                  hint: t('settings.schedules.fromTemplateHint'),
                },
                {
                  value: 'custom',
                  title: t('settings.schedules.custom'),
                  hint: t('settings.schedules.customHint'),
                },
              ]}
              onChange={(value) => {
                setAssignment({
                  ...assignment,
                  templateId:
                    value === 'custom'
                      ? null
                      : (props.templates.find((row) => row.active)?.id ?? null),
                });
              }}
            />

            {assignment.templateId !== null ? (
              <Field label={t('settings.schedules.column.schedule')}>
                <Select
                  aria-label={t('settings.schedules.column.schedule')}
                  value={assignment.templateId}
                  onChange={(event) => {
                    setAssignment({ ...assignment, templateId: event.target.value });
                  }}
                  options={props.templates
                    .filter((row) => row.active)
                    .map((row) => ({
                      value: row.id,
                      label:
                        row.type === 'flex'
                          ? `${row.name} · ${t('settings.schedules.type.flex')}`
                          : `${row.name} · ${String(sum(row.days))}h`,
                    }))}
                />
              </Field>
            ) : (
              <>
                <TypePicker
                  value={assignment.type}
                  onChange={(type) => {
                    setAssignment({
                      ...assignment,
                      type,
                      days: type === 'flex' ? [...EMPTY_DAYS] : assignment.days,
                    });
                  }}
                />
                {assignment.type === 'flex' ? (
                  <Field
                    label={t('settings.schedules.weekly')}
                    note={t('settings.schedules.flexNote')}
                  >
                    <Stepper
                      value={String(assignment.weeklyHours)}
                      min={0}
                      max={80}
                      step={0.5}
                      aria-label={t('settings.schedules.weekly')}
                      decrementLabel={t('common.decrease')}
                      incrementLabel={t('common.increase')}
                      onChange={(value) => {
                        setAssignment({ ...assignment, weeklyHours: Number(value) || 0 });
                      }}
                    />
                  </Field>
                ) : (
                  <DayGrid
                    days={assignment.days}
                    names={days}
                    onChange={(next) => {
                      setAssignment({ ...assignment, days: next, weeklyHours: sum(next) });
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </EditorDialog>
    </div>
  );
}

/** The two schedule types, as the mockup's radio cards. Shared by both editors. */
function TypePicker({
  value,
  onChange,
}: {
  readonly value: WorkScheduleType;
  readonly onChange: (type: WorkScheduleType) => void;
}) {
  const t = useT();
  return (
    <RadioCards
      label={t('settings.schedules.column.type')}
      value={value}
      options={[
        {
          value: 'fix',
          title: t('settings.schedules.type.fix'),
          hint: t('settings.schedules.fixHint'),
        },
        {
          value: 'flex',
          title: t('settings.schedules.type.flex'),
          hint: t('settings.schedules.flexHint'),
        },
      ]}
      onChange={(next) => {
        onChange(next === 'flex' ? 'flex' : 'fix');
      }}
    />
  );
}

/**
 * ⚠️⚠️ **A RADIO GROUP, DRAWN AS CARDS — AND OURS WAS A DROPDOWN.** The two options differ in what
 * they MEAN, and each card carries the sentence that says so; a select hides both explanations
 * behind a click, which is one way a workspace ends up with flex schedules it did not intend.
 *
 * ⚠️ **Real `<input type="radio">` elements, not the mockup's buttons.** The mockup draws its own
 * dot because it has no form primitives; a real radio group gets arrow-key movement, the group name
 * announced once, and the checked state for free — and looks identical.
 */
function RadioCards({
  label,
  value,
  options,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly { value: string; title: string; hint: string }[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-[12px] font-medium">{label}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors ${
            value === option.value ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'
          }`}
        >
          <input
            type="radio"
            className="accent-primary mt-0.5 size-4 shrink-0"
            checked={value === option.value}
            onChange={() => {
              onChange(option.value);
            }}
          />
          <span className="flex min-w-0 flex-col">
            <span className="text-[12.5px] font-medium">{option.title}</span>
            <span className="text-muted-foreground text-[11.5px]">{option.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * Seven steppers and a running weekly total — the mockup's *Napi óraszám*.
 *
 * ⚠️ **`Stepper`, not `<input type="number">`.** The mockup uses the browser's; settled control 5
 * says ours, and the reason is the one it was settled for — a native spinner is drawn by the
 * operating system and takes no theme at all.
 */
function DayGrid({
  days,
  names,
  onChange,
}: {
  readonly days: readonly number[];
  readonly names: readonly string[];
  readonly onChange: (days: readonly number[]) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium">{t('settings.schedules.days')}</span>
        <div className="flex-1" />
        {/* ⚠️ The total is derived, never typed: two figures for one fact drift the first time
            somebody edits a day and forgets the sum. */}
        <span className="text-muted-foreground text-[11.5px] tabular-nums">
          {t('settings.schedules.weekTotal', { hours: String(sum(days)) })}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
        {names.map((name, index) => (
          <div key={name} className="flex flex-col gap-1">
            <span className="text-muted-foreground text-center text-[11px]">{name}</span>
            <Stepper
              value={String(days[index] ?? 0)}
              min={0}
              max={24}
              step={0.5}
              aria-label={name}
              decrementLabel={t('common.decrease')}
              incrementLabel={t('common.increase')}
              onChange={(value) => {
                const next = [...days];
                next[index] = Number(value) || 0;
                onChange(next);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
